import { useQuery } from '@tanstack/react-query';
import { FC } from 'react';
import styled from 'styled-components';

import { Spinner } from '@/components/Button';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import Text from '@/components/Text';
import { ChannelGroupResource } from '@/types/resources';
import { api } from '@/utils/api';

import { Box, ChannelIcon, HeadingHolder } from './ChannelsList';

const Heading = styled(Text)`
  span {
    color: ${({ theme }) => theme.colors.gray};
  }
`;

const Channels = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 22px;
`;

const Channel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ReelsCount = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 4px;
  height: 16px;
  min-width: 38px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.lightGray};
`;

const StyledSpinner = styled(Spinner)`
  width: 10px;
  height: 10px;
  margin: 0;
`;

interface Props {
  currentGroup?: ChannelGroupResource;
  isWhite?: boolean;
}

const ReelsChannelsList: FC<Props> = ({ currentGroup, isWhite }) => {
  const { data, isLoading } = useQuery(
    [
      'group_reels_counter',
      {
        group_id: currentGroup?.id,
      },
    ],
    () =>
      api.get<never, { channelCounts: { [channelId: number]: number } }>(
        '/v1/distribution/group-reels-counter',
        {
          params: {
            group_id: currentGroup?.id,
          },
        },
      ),
    {
      staleTime: 30_000,
    },
  );

  const isInvalid = Object.values(data?.channelCounts || {}).some(
    (count) => count > 29,
  );

  return (
    <>
      <Box $isWhite={isWhite}>
        <HeadingHolder>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="8" cy="8" r="7.5" fill="#E9E9E9" stroke="#E9E9E9" />
            <path
              d="M7.25 5.73529V4.5H8.75V5.73529H7.25ZM7.25 11.5V6.55882H8.75V11.5H7.25Z"
              fill="currentColor"
            />
          </svg>
          <Heading $type="captionSemibold">
            Channel List of <span>({currentGroup?.name})</span>
          </Heading>
        </HeadingHolder>
        {!!currentGroup?.channels?.length && (
          <Channels>
            {currentGroup.channels.map((channel) => (
              <Channel key={channel.id}>
                {!!channel.thumbnail && (
                  <ChannelIcon
                    src={channel.thumbnail}
                    alt={`${channel.name} icon`}
                  />
                )}
                <Text as="span" $type="smallBold">
                  {channel.name}
                </Text>
                <ReelsCount>
                  {isLoading || !data ? (
                    <StyledSpinner />
                  ) : (
                    <Text
                      $type="extraSmall"
                      $color={
                        data.channelCounts[channel.id]! > 28 ? 'error' : 'black'
                      }
                    >
                      {30 - (data.channelCounts[channel.id] ?? 0)} reels
                      remaining
                    </Text>
                  )}
                </ReelsCount>
              </Channel>
            ))}
          </Channels>
        )}
      </Box>
      {isInvalid && (
        <ValidationMessage>Channel(s) reached posts limit</ValidationMessage>
      )}
    </>
  );
};

export default ReelsChannelsList;
